import type { FeatureExtractionContextType } from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { DrawingTool } from '@trimble/geospatial';
import type { ActionMenuItem } from '@trimble/ui-components/solidjs';
import { Accessor, createMemo } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';

import type { MainToolbarEntry } from './MainToolbar';

const CAD_DRAWING_LAST_TOOL_STORAGE_KEY = 'main-toolbar-cad-drawing-last-tool';

function drawingToolIcon(tool: DrawingTool): string {
  switch (tool) {
    case DrawingTool.Point:
      return 'tc-icon-circle';
    case DrawingTool.CompoundCurve:
      return 'tc-icon-line';
    case DrawingTool.Rectangle:
      return 'tc-icon-square';
    case DrawingTool.Polygon:
      return 'tc-icon-markup-polygon';
    default:
      throw new UnreachableCaseError(tool);
  }
}

function drawingToolLabel(tool: DrawingTool): string {
  switch (tool) {
    case DrawingTool.Point:
      return t('mobileMapping.cadEditing.tools.point');
    case DrawingTool.CompoundCurve:
      return t('mobileMapping.cadEditing.tools.compoundCurve');
    case DrawingTool.Rectangle:
      return t('mobileMapping.cadEditing.tools.rectangle');
    case DrawingTool.Polygon:
      return t('mobileMapping.cadEditing.tools.polygon');
    default:
      throw new UnreachableCaseError(tool);
  }
}

function toggleDrawingTool(
  tool: DrawingTool,
  featureExtraction: FeatureExtractionContextType
): void {
  if (featureExtraction.desiredDrawingTool() === tool) {
    featureExtraction.setDesiredDrawingTool(undefined);
    return;
  }
  if (featureExtraction.selectedLayer() === undefined) {
    NotificationEventBus.emit({
      type: 'info',
      message: t('mobileMapping.cadEditing.noActiveLayer'),
    });
    return;
  }
  featureExtraction.setDesiredDrawingTool(tool);
}

function drawingToolMenuItem(
  tool: DrawingTool,
  featureExtraction: FeatureExtractionContextType
): ActionMenuItem {
  return {
    id: tool,
    type: 'action',
    label: drawingToolLabel(tool),
    icon: drawingToolIcon(tool),
    onClick: (): void => {
      toggleDrawingTool(tool, featureExtraction);
    },
  };
}

export function useCadEditingToolbarItems(
  featureExtraction: FeatureExtractionContextType,
  isCadEditingEligible: Accessor<boolean>
): Accessor<MainToolbarEntry[]> {
  const items = createMemo<MainToolbarEntry[]>(() => {
    if (!isCadEditingEligible()) {
      return [];
    }

    const entries: MainToolbarEntry[] = [];

    const allowedTools = featureExtraction.allowedDrawingTools();
    const desiredTool = featureExtraction.desiredDrawingTool();
    const isDrawingActive =
      desiredTool !== undefined && allowedTools.includes(desiredTool);

    if (allowedTools.length === 1 && allowedTools[0] === DrawingTool.Point) {
      entries.push({
        id: 'drawing-tool',
        type: 'button',
        iconName: drawingToolIcon(DrawingTool.Point),
        ariaLabel: t('mobileMapping.cadEditing.drawingTool'),
        tooltip: t('mobileMapping.cadEditing.drawingTool'),
        pressed: isDrawingActive,
        action: () => toggleDrawingTool(DrawingTool.Point, featureExtraction),
      });
      return entries;
    }

    if (allowedTools.length > 1) {
      const firstTool = allowedTools[0];
      // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
      if (firstTool === undefined) {
        return entries;
      }
      const anchorTool =
        desiredTool !== undefined && allowedTools.includes(desiredTool)
          ? desiredTool
          : firstTool;
      entries.push({
        id: 'drawing-tool',
        type: 'split-dropdown',
        storageKey: CAD_DRAWING_LAST_TOOL_STORAGE_KEY,
        anchorIcon: drawingToolIcon(anchorTool),
        tooltip: t('mobileMapping.cadEditing.drawingTool'),
        items: allowedTools.map((tool) =>
          drawingToolMenuItem(tool, featureExtraction)
        ),
        pressed: isDrawingActive,
        isAnchorActive: isDrawingActive,
        onAnchorActiveClick: () =>
          featureExtraction.setDesiredDrawingTool(undefined),
      });
    }

    return entries;
  });

  return items;
}
